import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureFlags } from '@inyova/models';
import * as dayjs from 'dayjs';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as AppRootActions from '@app/app.actions';
import * as fromAppRoot from '@app/app.reducers';

import { BANK_HOLIDAYS, CH_LIST_OF_HOLIDAYS, DE_IDNOW_HOLIDAYS } from '@app/app.constants';
import { LanguageService } from '@shared/services/language.service';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  selector: 'app-dialog-reminder',
  templateUrl: './dialog-reminder.component.html',
  styleUrls: ['./dialog-reminder.component.scss'],
  standalone: false
})
export class DialogReminderComponent implements OnInit, OnDestroy, AfterViewInit {
  protected readonly onDestroy$ = new Subject<void>();
  @ViewChild('reminderTime') reminderTime: ElementRef;
  @Input() askedForReminder: boolean;
  date: Date;
  day: string;
  validTime = true;
  showPastTimeError = false;
  initialHour = '09:00';
  currentLang: string;
  bankHolidays = BANK_HOLIDAYS;
  isBankHoliday = false;
  useAutoident = false;
  appLocation: string;

  constructor(
    private languageService: LanguageService,
    public http: HttpClient,
    private store: Store,
    private router: Router,
    private trackingService: TrackingService
  ) {
    this.currentLang = this.languageService.getRedirectLanguage();
    this.store.pipe(select(fromAppRoot.selectCustomer), takeUntil(this.onDestroy$)).subscribe((customer) => {
      this.appLocation = customer.app_location;
      this.useAutoident = customer.app_location === 'ch' && customer.enabled_features.includes(FeatureFlags.AUTOIDENT);
    });
  }

  ngOnInit(): void {
    this.date = new Date();
    this.setDateFormat();

    // add extra 15min
    const newDate = new Date(this.date.getTime() + 15 * 60000);
    const hours = newDate.getHours() < 10 ? `0${newDate.getHours()}` : newDate.getHours();
    const minutes = newDate.getMinutes() < 10 ? `0${newDate.getMinutes()}` : newDate.getMinutes();
    this.initialHour = `${hours}:${minutes}`;
  }

  ngAfterViewInit(): void {
    this.validateTime();
  }

  otherDay(val: number): void {
    this.trackActivity('[Button] Change reminder day');
    this.date.setDate(this.date.getDate() + val);
    this.setDateFormat();
    this.validateTime();
  }

  isPast(): boolean {
    // Get just day & month & year information
    const currentDate = new Date().toLocaleString('en', {
      year: 'numeric',
      day: 'numeric',
      month: 'short'
    });

    // Check if day before is a holiday

    // Get just day & month & year information
    const newDate = this.date.toLocaleString('en', {
      year: 'numeric',
      day: 'numeric',
      month: 'short'
    });
    // Ignore time of the day in the condition bellow
    return new Date(newDate) > new Date(currentDate);
  }

  isFar(): boolean {
    const inSevenDays = new Date();
    inSevenDays.setDate(new Date().getDate() + 6);
    return this.date < inSevenDays;
  }

  setDateFormat(): void {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    };

    this.day = this.date.toLocaleDateString(this.currentLang, options);
    this.languageFormat(this.day);
  }

  languageFormat(day: string): void {
    // english date - Wednesday, 2/24/2021
    // german date - Mittwoch, 24.2.2021
    // french date - mercredi 24/02/2021
    const daySeperator = this.currentLang !== 'fr' ? ',' : ' ';
    const dateSeperator = this.currentLang !== 'de' ? '/' : '.';

    let dayString = day.toLocaleString().split(daySeperator)[1].trim();

    // if it's not EN change day month place
    if (this.currentLang !== 'en') {
      const splitedDay = dayString.split(dateSeperator);
      dayString = `${splitedDay[0]}.${splitedDay[1]}`;
    }
  }

  close(): void {
    this.store.dispatch(AppRootActions.showReminderDialog({ reminderModalOpened: false }));
    this.trackActivity('[Button] Close reminder dialog');
    if (this.askedForReminder) {
      this.trackActivity('[Modal] Reminder closed');
      // eslint-disable-next-line no-void
      void this.router.navigate(['/back']);
    }
  }

  save(): void {
    this.trackActivity('[Button] Save reminder');
    this.validateTime();
    if (this.validTime) {
      this.store.dispatch(AppRootActions.updateReminder({ reminder: this.date }));
    }
  }

  isHoliday(): boolean {
    const holidayList = this.appLocation === 'ch' ? CH_LIST_OF_HOLIDAYS : DE_IDNOW_HOLIDAYS;
    return holidayList.includes(dayjs(this.date).format('MM/DD/YYYY'));
  }

  validateTime(): void {
    if (!this.useAutoident) {
      this.isBankHoliday = false;
      const morningBankHolidays = this.appLocation === 'ch' ? [] : ['01/01/2022'];
      const noMorningService = morningBankHolidays.includes(dayjs(this.date).format('MM/DD/YYYY'));
      this.isBankHoliday = this.bankHolidays.includes(dayjs(this.date).format('MM/DD/YYYY'));
      const pickedTime: HTMLInputElement = this.reminderTime.nativeElement;
      const pickedTimeArray = pickedTime.value.split(':');
      const pickedHour = pickedTimeArray[0];
      const pickedMin = Number(pickedTimeArray[1]);
      const customerTime = `${pickedHour}:${pickedMin}`;
      this.trackActivity(`[Input] Picked reminder time: ${pickedHour}:${pickedMin}`);
      const beginningTime = noMorningService ? '16:00' : '08:00';
      const normalEndTime = '23:59';
      const holidayEndTime = '15:30';
      const endTime = this.isBankHoliday ? holidayEndTime : normalEndTime;

      if (this.isHoliday()) {
        this.validTime = false;
        return;
      }

      if (customerTime < beginningTime || customerTime > endTime) {
        this.validTime = false;
        this.showPastTimeError = false;
        return;
      }
      this.validTime = true;

      const currentTime = new Date();
      const currentDate = currentTime.getDate();
      const isToday = currentDate === this.date.getDate();

      if (!isToday) {
        return;
      }

      // set hour & minutes
      this.date.setHours(Number(pickedHour));
      this.date.setMinutes(pickedMin);

      if (this.date <= currentTime) {
        this.validTime = false;
        this.showPastTimeError = true;
        return;
      }
    }

    this.validTime = true;
  }

  trackActivity(activity: string): void {
    this.trackingService.trackActivity(activity);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
